import React, { useState, useEffect, useRef } from "react";
import {
    Button, Card, CardBody, CardHeader, Container, Modal, ModalBody, ModalFooter,
    ModalHeader, Form, FormGroup, Input, Label,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Spinner
} from "reactstrap";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { Toast } from "primereact/toast";
import axios from "axios";
import { API_BASE_URL } from "../../../constants";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { Edit, MoreVertical, Trash } from "lucide-react";

const DesignationList = () => {
    const [designations, setDesignations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [modal, setModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [departmentId, setDepartmentId] = useState("");
    const [selectedDesignation, setSelectedDesignation] = useState(null);
    const [designationToDelete, setDesignationToDelete] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(null);
    const toggleDropdown = (id) => setDropdownOpen(dropdownOpen === id ? null : id);
    
    const toast = useRef(null);

    const toggle = () => setModal(!modal);
    const toggleDeleteModal = () => setDeleteModal(!deleteModal);


    const breadcrumbItems = [
        { title: "HR", link: "/" },
        { title: "Designations", link: "#" },
    ];

    const fetchDesignations = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/designations/list`, {
                headers: { Authorization: "Bearer " + localStorage.getItem("token") },
            });
            setDesignations(response.data.data || []);
            setTotalRecords(response.data.data.length || 0);
        } catch (error) {
            console.error("Error fetching designations:", error);
        }
        setLoading(false);
    };

    const fetchDepartments = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/departments/list`, {
                headers: { Authorization: "Bearer " + localStorage.getItem("token") },
            });

            console.log("Full API Response:", response.data);

            if (response.data && response.data.status === "success") {
                setDepartments(response.data.data || []);
            } else {
                console.error("Unexpected API response format:", response.data);
            }
        } catch (error) {
            console.error("Error fetching departments:", error);
        }
    };

    useEffect(() => {
        fetchDesignations();
        fetchDepartments();
    }, []);

    const onPageChange = (event) => {
        setFirst(event.first);
        setRows(event.rows);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const data = {
            name,
            description,
            department_id: departmentId || null,
            office_id: 1,
            added_by: localStorage.getItem("user_id"),
        };

        setLoading(true);
        try {
            if (selectedDesignation) {
                await axios.post(`${API_BASE_URL}/designations/update/${selectedDesignation.id}`, data, {
                    headers: { Authorization: "Bearer " + localStorage.getItem("token") },
                });
                toast.current.show({ severity: 'success', summary: 'Updated', detail: 'Designation updated successfully', life: 3000 });
            } else {
                await axios.post(`${API_BASE_URL}/designations/add`, data, {
                    headers: { Authorization: "Bearer " + localStorage.getItem("token") },
                });
                toast.current.show({ severity: 'success', summary: 'Added', detail: 'Designation added successfully', life: 3000 });
            }
            setName("");
            setDescription("");
            setDepartmentId("");
            setSelectedDesignation(null);
            toggle();
            fetchDesignations();
        } catch (error) {
            console.error("Error details:", error.response?.data);
            toast.current.show({ severity: 'error', summary: 'Error', detail: error.response?.data?.message || 'Failed to save designation', life: 3000 });
        }
        setLoading(false);
    };

    const handleDelete = async () => {
        if (!designationToDelete) return;
        try {
            await axios.delete(`${API_BASE_URL}/designations/delete/${designationToDelete.id}`, {
                headers: { Authorization: "Bearer " + localStorage.getItem("token") },
            });
            toast.current.show({ severity: "success", summary: "Deleted", detail: "Designation deleted successfully", life: 3000 });
            fetchDesignations();
        } catch (error) {
            toast.current.show({ severity: "error", summary: "Error", detail: "Failed to delete designation", life: 3000 });
        }
        setDesignationToDelete(null);
        toggleDeleteModal();
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Designations" breadcrumbItems={breadcrumbItems} />
                    <Card>
                        <CardHeader className="d-flex justify-content-between align-items-center">
                            <h4 className="card-title mb-0 flex-grow-1">Designations List</h4>
                            <Button onClick={toggle} color="primary">Add Designation</Button>
                        </CardHeader>
                        <CardBody>
                            <DataTable value={designations.slice(first, first + rows)} loading={loading} stripedRows>
                                <Column header="Si No" body={(rowData, { rowIndex }) => rowIndex + 1 + first} />
                                <Column header="Name" field="name" sortable />
                                <Column header="Department" body={(rowData) => rowData.department?.name || "N/A"} sortable />
                                <Column header="Description" field="description" sortable />
                                <Column 
                                header="Actions" 
                                body={(rowData) => (
                                    <Dropdown 
                                    isOpen={dropdownOpen === rowData.id} 
                                    toggle={() => toggleDropdown(rowData.id)}
                                    >
                                    <DropdownToggle tag="button" className="btn btn-light">
                                        <MoreVertical size={20} />
                                    </DropdownToggle>
                                    <DropdownMenu>
                                        <DropdownItem 
                                        onClick={() => {
                                            setSelectedDesignation(rowData);
                                            setName(rowData.name);
                                            setDescription(rowData.description);
                                            setDepartmentId(rowData.department_id || "");
                                            toggle();
                                        }}
                                        >
                                        <Edit size={16} className="me-2" />
                                        Edit
                                        </DropdownItem>
                                        <DropdownItem 
                                        onClick={() => {
                                            setDesignationToDelete(rowData);
                                            toggleDeleteModal();
                                        }}
                                        className="text-danger"
                                        >
                                        <Trash size={16} className="me-2" />
                                        Delete
                                        </DropdownItem>
                                    </DropdownMenu>
                                    </Dropdown>
                                )}
                                />
                            </DataTable>
                            <Paginator first={first} rows={rows} totalRecords={totalRecords} onPageChange={onPageChange} rowsPerPageOptions={[5, 10, 25, 50]} />
                        </CardBody>
                    </Card>
                </Container>
            </div>

            {/* Modal for Adding/Editing Designation */}
            <Modal isOpen={modal} centered toggle={toggle}>
                <Form onSubmit={handleSubmit}>
                    <ModalHeader toggle={toggle}>{selectedDesignation ? 'Edit Designation' : 'Add Designation'}</ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <Label>Name</Label>
                            <Input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
                        </FormGroup>
                        <FormGroup>
                            <Label>Department</Label>
                            <Input type="select" value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
                                <option value="">Select Department</option>
                                {departments.map(dept => (
                                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                                ))}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label>Description</Label>
                            <Input type="textarea" value={description} onChange={(e) => setDescription(e.target.value)} />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={toggle}>Cancel</Button>
                        <Button color="primary" type="submit">{selectedDesignation ? "Update" : "Add"}
                        {loading && <Spinner className="ms-1" size="sm" >Loading...</Spinner>}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>

            <Modal isOpen={deleteModal} centered toggle={toggleDeleteModal}>
                <ModalHeader toggle={toggleDeleteModal}>Confirm Delete</ModalHeader>
                <ModalBody>
                    Are you sure you want to delete this department?
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleDeleteModal}>Cancel</Button>
                    <Button color="danger" onClick={handleDelete}>Delete
                        {loading && <Spinner className="ms-1" size="sm" >Loading...</Spinner>}
                    </Button>
                </ModalFooter>
            </Modal>
            <Toast ref={toast} />
        </React.Fragment>
    );
};

export default DesignationList;
